import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { Button } from "../../@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";

export default function BatchEditPathwayEnrollmentDialog({
  open,
  onOpenChange,
  enrolments,
  pathways,
  onUpdated,
}) {
  const { getToken } = useAuth();
  const [newPathwayId, setNewPathwayId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNewPathwayId("");
  }, [open]);

  const selected = useMemo(() => {
    const rows = Array.isArray(enrolments) ? enrolments : [];
    return rows.filter(Boolean);
  }, [enrolments]);

  const userIds = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const e of selected) {
      const id = e?.user_id ?? e?.userId;
      if (!id) continue;
      const key = String(id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(key);
    }
    return out;
  }, [selected]);

  const oldPathwayIds = useMemo(() => {
    const ids = new Set();
    selected.forEach((e) => {
      const id = e?.pathway_id ?? e?.pathwayId;
      if (id === 0 || id) ids.add(Number(id));
    });
    return Array.from(ids).filter((n) => Number.isFinite(n));
  }, [selected]);

  const oldPathwayId = oldPathwayIds.length === 1 ? oldPathwayIds[0] : null;
  const hasMixedOldPathways = oldPathwayIds.length > 1;

  const pathwayOptions = useMemo(() => {
    const list = Array.isArray(pathways) ? pathways : [];
    return list
      .map((p) => ({
        id: p?.id,
        name: p?.name || p?.title,
      }))
      .filter((p) => p.id != null && String(p.name || "").trim().length);
  }, [pathways]);

  const oldPathwayName = useMemo(() => {
    if (oldPathwayId == null) {
      return selected?.[0]?.pathway || "Not available";
    }
    const match = pathwayOptions.find((p) => Number(p.id) === Number(oldPathwayId));
    return match?.name || selected?.[0]?.pathway || "Not available";
  }, [pathwayOptions, oldPathwayId, selected]);

  const selectedCount = selected.length;

  const canSubmit =
    !saving &&
    selectedCount >= 2 &&
    userIds.length >= 2 &&
    oldPathwayId != null &&
    Boolean(newPathwayId) &&
    Number(newPathwayId) !== Number(oldPathwayId);

  const handleSave = async () => {
    if (selectedCount < 2) {
      toast.error("Select at least 2 enrolments.");
      return;
    }
    if (userIds.length === 0) {
      toast.error("Missing user ids for selected enrolments.");
      return;
    }
    if (hasMixedOldPathways || oldPathwayId == null) {
      toast.error("Bulk edit requires enrolments from the same current pathway.");
      return;
    }
    if (!newPathwayId) {
      toast.error("Please select a new pathway.");
      return;
    }
    if (Number(newPathwayId) === Number(oldPathwayId)) {
      toast.error("Please select a different pathway.");
      return;
    }

    setSaving(true);
    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const payload = {
        user_ids: userIds,
        old_pathway_id: Number(oldPathwayId),
        new_pathway_id: Number(newPathwayId),
      };

      const res = await axios.put(
        api_endpoint + "/pathway/enrollment/edit/bulk",
        payload,
        reqConfig
      );

      const data = res?.data;
      const totalUpdated =
        data?.total_updated != null ? Number(data.total_updated) : null;
      const totalErrors =
        data?.total_errors != null ? Number(data.total_errors) : null;

      const updatedCount = Number.isFinite(totalUpdated)
        ? totalUpdated
        : Array.isArray(data?.updated_user_ids)
          ? data.updated_user_ids.length
          : userIds.length;

      if (Number.isFinite(totalErrors) && totalErrors > 0) {
        toast.success(
          `Updated ${updatedCount} enrolment${updatedCount === 1 ? "" : "s"}. ${totalErrors} failed.`
        );
      } else {
        toast.success(
          `Updated ${updatedCount} enrolment${updatedCount === 1 ? "" : "s"} successfully.`
        );
      }

      onOpenChange?.(false);
      onUpdated?.(data);
    } catch (error) {
      console.error("Failed to bulk edit pathway enrolments", error);
      toast.error("Failed to update enrolments. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const isDisabled = saving;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md h-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-(--neutral-900)">
            Edit Enrolments
          </DialogTitle>
          <DialogDescription className="font-body text-(--neutral-700)">
            Move <span className="font-semibold">{selectedCount}</span> users from{" "}
            <span className="font-semibold">{oldPathwayName}</span> to a new pathway.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {hasMixedOldPathways ? (
            <div className="rounded-md border border-(--color-destructive) bg-(--surface-base) p-3 text-sm text-(--neutral-800)">
              Bulk edit requires all selected enrolments to be from the same current pathway.
            </div>
          ) : null}

          <div className="space-y-2">
            <div className="font-body text-sm text-(--neutral-700)">
              New Pathway
            </div>
            <Select value={newPathwayId} onValueChange={setNewPathwayId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a pathway" />
              </SelectTrigger>
              <SelectContent>
                {pathwayOptions.map((p) => (
                  <SelectItem key={String(p.id)} value={String(p.id)}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={isDisabled}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant=""
              onClick={handleSave}
              disabled={isDisabled || !canSubmit}
            >
              {saving ? <ClipLoader size={18} color="#fff" /> : "Save"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
